import { LucideProvider } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

/**
 * App-wide default stroke width for every lucide icon, adapted to the whole-app
 * CSS zoom. Lucide's built-in default is 2.0; the house value is the thinner
 * 1.3 (1.0 once zoomed past 1.5×, so strokes don't look chunky when the UI is
 * scaled up). Every `<LucideIcon />` rendered under this provider inherits the
 * value via `LucideContext`, and a per-icon `strokeWidth={…}` prop still wins —
 * so intentional overrides (StatusBar branch icon, tiny plugin-slot glyphs)
 * keep working as plain props.
 *
 * Mounted once at the render chokepoint in `main.tsx`, above all three root
 * variants (App / MobileApp / StandalonePluginPanel), so nothing has to be
 * registered per-icon and no root can silently fall back to 2.0.
 *
 * Whole-app zoom lives on `document.documentElement.style.zoom` (Cmd+/- mutates
 * it). There's no zoom event, so we observe the `<html>` `style` attribute —
 * one observer for the whole app, replacing the per-icon polling timers the old
 * `useZoomStrokeWidth` hook spun up.
 */
const NORMAL = 1.3;
const ZOOMED = 1.0;

export function IconStrokeProvider({ children }: { children: ReactNode }) {
  const [sw, setSw] = useState(NORMAL);

  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const zoom = parseFloat(getComputedStyle(root).zoom || '1') || 1;
      setSw(zoom >= 1.5 ? ZOOMED : NORMAL);
    };
    apply();
    const mo = new MutationObserver(apply);
    mo.observe(root, { attributes: true, attributeFilter: ['style'] });
    return () => mo.disconnect();
  }, []);

  return <LucideProvider strokeWidth={sw}>{children}</LucideProvider>;
}
